import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Building2, Save, Loader2, ArrowLeft } from 'lucide-react';
import { API } from '../App';

const Profile = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    business_name: '',
    website: '',
    business_description: '',
    industry: '',
    focus_keywords: '',
    city: '',
    state: '',
    phone: '',
    address: '',
    services_offered: '',
    years_in_business: '',
    target_audience: '',
    brand_voice: 'professional',
    unique_selling_points: '',
    owner_name: '',
    expert_credentials: '',
    services_list: [],
    service_areas: [],
    target_audiences_list: [],
    logos: [],
    logo_position: 'bottom-right',
    cta_library: [],  // {name, text, audience}
    links_library: []  // {name, url, description}
  });
  const [newService, setNewService] = useState('');
  const [newServiceArea, setNewServiceArea] = useState('');
  const [newTargetAudience, setNewTargetAudience] = useState('');
  const [logoName, setLogoName] = useState('');
  const [newCta, setNewCta] = useState({ name: '', text: '', audience: '' });
  const [newLink, setNewLink] = useState({ name: '', url: '', description: '' });
  const [generatingCta, setGeneratingCta] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editingCta, setEditingCta] = useState(null);
  const [generatingLinks, setGeneratingLinks] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingUSP, setGeneratingUSP] = useState(false);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/profile/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setProfile({
            business_name: data.business_name || '',
            website: data.website || '',
            business_description: data.business_description || '',
            industry: data.industry || '',
            focus_keywords: data.focus_keywords || '',
            city: data.city || '',
            state: data.state || '',
            phone: data.phone || '',
            address: data.address || '',
            services_offered: data.services_offered || '',
            years_in_business: data.years_in_business || '',
            target_audience: data.target_audience || '',
            brand_voice: data.brand_voice || 'professional',
            unique_selling_points: data.unique_selling_points || '',
            services_list: data.services_list || [],
            service_areas: data.service_areas || [],
            target_audiences_list: data.target_audiences_list || [],
            logos: data.logos || [],
            logo_position: data.logo_position || 'bottom-right',
            cta_library: data.cta_library || [],
            links_library: data.links_library || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addService = () => {
    if (newService.trim()) {
      setProfile(prev => ({
        ...prev,
        services_list: [...prev.services_list, newService.trim()]
      }));
      setNewService('');
    }
  };

  const removeService = (index) => {
    setProfile(prev => ({
      ...prev,
      services_list: prev.services_list.filter((_, i) => i !== index)
    }));
  };

  const addServiceArea = () => {
    if (newServiceArea.trim()) {
      setProfile(prev => ({
        ...prev,
        service_areas: [...prev.service_areas, newServiceArea.trim()]
      }));
      setNewServiceArea('');
    }
  };

  const removeServiceArea = (index) => {
    setProfile(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter((_, i) => i !== index)
    }));
  };

  const addTargetAudience = () => {
    if (newTargetAudience.trim()) {
      setProfile(prev => ({
        ...prev,
        target_audiences_list: [...prev.target_audiences_list, newTargetAudience.trim()]
      }));
      setNewTargetAudience('');
    }
  };

  const removeTargetAudience = (index) => {
    setProfile(prev => ({
      ...prev,
      target_audiences_list: prev.target_audiences_list.filter((_, i) => i !== index)
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if already have 3 logos
      if (profile.logos && profile.logos.length >= 3) {
        alert('Maximum 3 logos allowed. Please remove one first.');
        return;
      }
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo file size must be less than 2MB');
        return;
      }
      
      if (!logoName.trim()) {
        alert('Please enter a name for this logo');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        const newLogo = {
          name: logoName.trim(),
          url: base64String,
          active: !profile.logos || profile.logos.length === 0 // First logo is active by default
        };
        
        setProfile(prev => ({
          ...prev,
          logos: [...(prev.logos || []), newLogo]
        }));
        setLogoName('');
        // Reset file input
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const setActiveLogo = (index) => {
    setProfile(prev => ({
      ...prev,
      logos: prev.logos.map((logo, i) => ({
        ...logo,
        active: i === index
      }))
    }));
  };

  const removeLogo = (index) => {
    if (!confirm('Remove this logo?')) return;
    
    setProfile(prev => {
      const newLogos = prev.logos.filter((_, i) => i !== index);
      // If we removed the active logo, make the first one active
      if (newLogos.length > 0 && !newLogos.some(l => l.active)) {
        newLogos[0].active = true;
      }
      return {
        ...prev,
        logos: newLogos
      };
    });
  };

  // CTA Library functions
  const generateCtaText = async () => {
    if (!newCta.name.trim()) {
      alert('Please enter a CTA name first');
      return;
    }
    
    setGeneratingCta(true);
    try {
      const response = await fetch(`${API}/generate-cta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cta_name: newCta.name,
          audience: newCta.audience || 'General',
          business_name: profile.business_name,
          services: profile.services_list,
          city: profile.city
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewCta(prev => ({ ...prev, text: data.cta_text }));
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Error generating CTA: ${errorData.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error generating CTA:', error);
      alert('Error generating CTA: Network error. Please check your connection and try again.');
    } finally {
      setGeneratingCta(false);
    }
  };

  // AI Generate Business Description
  const generateDescription = async () => {
    if (!profile.business_name || !profile.industry) {
      alert('Please enter your Business Name and Industry first');
      return;
    }
    
    setGeneratingDescription(true);
    try {
      const response = await fetch(`${API}/generate-description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: profile.business_name,
          industry: profile.industry,
          city: profile.city,
          services: profile.services_list,
          existing_text: profile.business_description
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, business_description: data.description }));
      } else {
        alert('Error generating description. Please try again.');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      alert('Error generating description. Please try again.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  // AI Generate Unique Selling Points
  const generateUSP = async () => {
    if (!profile.business_name) {
      alert('Please enter your Business Name first');
      return;
    }
    
    setGeneratingUSP(true);
    try {
      const response = await fetch(`${API}/generate-usp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: profile.business_name,
          industry: profile.industry,
          city: profile.city,
          services: profile.services_list,
          existing_text: profile.unique_selling_points
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, unique_selling_points: data.usp }));
      } else {
        alert('Error generating USP. Please try again.');
      }
    } catch (error) {
      console.error('Error generating USP:', error);
      alert('Error generating USP. Please try again.');
    } finally {
      setGeneratingUSP(false);
    }
  };

  // AI Generate Expert Credentials
  const generateCredentials = async () => {
    if (!profile.owner_name && !profile.business_name) {
      alert('Please enter the Owner Name or Business Name first');
      return;
    }
    
    setGeneratingCredentials(true);
    try {
      const response = await fetch(`${API}/generate-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_name: profile.owner_name,
          business_name: profile.business_name,
          industry: profile.industry,
          years_in_business: profile.years_in_business,
          existing_text: profile.expert_credentials
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, expert_credentials: data.credentials }));
      } else {
        alert('Error generating credentials. Please try again.');
      }
    } catch (error) {
      console.error('Error generating credentials:', error);
      alert('Error generating credentials. Please try again.');
    } finally {
      setGeneratingCredentials(false);
    }
  };

  const addCta = () => {
    if (newCta.name.trim() && newCta.text.trim()) {
      setProfile(prev => ({
        ...prev,
        cta_library: [...(prev.cta_library || []), {
          id: Date.now().toString(),
          name: newCta.name.trim(),
          text: newCta.text.trim(),
          audience: newCta.audience.trim() || 'General'
        }]
      }));
      setNewCta({ name: '', text: '', audience: '' });
    }
  };

  // Links Library functions
  const addLink = () => {
    if (newLink.name.trim() && newLink.url.trim()) {
      setProfile(prev => ({
        ...prev,
        links_library: [...(prev.links_library || []), {
          id: Date.now().toString(),
          name: newLink.name.trim(),
          url: newLink.url.trim(),
          description: newLink.description.trim() || ''
        }]
      }));
      setNewLink({ name: '', url: '', description: '' });
    }
  };

  const removeLink = (id) => {
    setProfile(prev => ({
      ...prev,
      links_library: prev.links_library.filter(link => link.id !== id)
    }));
  };

  const generateLinksFromWebsite = async () => {
    if (!user || !user.id) {
      alert('Please log in first to use this feature');
      return;
    }
    if (!profile.website) {
      alert('Please enter your website URL first in the Business Information section');
      return;
    }
    
    setGeneratingLinks(true);
    try {
      const response = await fetch(`${API}/generate-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          website_url: profile.website,
          user_id: user.id
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          links_library: data.links
        }));
        alert(`✅ Generated ${data.links_count} links from your website!`);
      } else {
        const error = await response.json().catch(() => ({}));
        alert('Error: ' + (error.detail || 'Failed to generate links. Please try again.'));
      }
    } catch (error) {
      console.error('Error generating links:', error);
      alert('Error generating links from website. Please check your connection and try again.');
    } finally {
      setGeneratingLinks(false);
    }
  };

  const startEditLink = (link) => {
    setEditingLink({ ...link });
  };

  const saveEditLink = () => {
    if (editingLink) {
      setProfile(prev => ({
        ...prev,
        links_library: prev.links_library.map(link => 
          link.id === editingLink.id ? editingLink : link
        )
      }));
      setEditingLink(null);
    }
  };

  const startEditCta = (cta) => {
    setEditingCta({ ...cta });
  };

  const saveEditCta = () => {
    if (editingCta) {
      setProfile(prev => ({
        ...prev,
        cta_library: prev.cta_library.map(cta => 
          cta.id === editingCta.id ? editingCta : cta
        )
      }));
      setEditingCta(null);
    }
  };

  const removeCta = (id) => {
    setProfile(prev => ({
      ...prev,
      cta_library: prev.cta_library.filter(cta => cta.id !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API}/profile?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...profile,
          years_in_business: profile.years_in_business ? parseInt(profile.years_in_business) : null
        })
      });

      if (response.ok) {
        alert('Profile saved successfully!');
        navigate('/dashboard');
      } else {
        alert('Error saving profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Business Profile | QuillFire - Set Up Your Business</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center space-x-2">
            <Building2 className="w-6 h-6 text-orange-600" />
            <span className="text-xl font-bold text-gray-900">Business Profile</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Profile</h1>
            <p className="text-gray-600">
              Tell us about your business so we can create content tailored specifically to you.
              This information will be used to generate personalized blogs that answer customer questions.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name <span className="text-gray-400">(recommended)</span>
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={profile.business_name}
                    onChange={handleChange}
                    
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., Sunrise Dental Care"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={profile.website}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry <span className="text-gray-400">(recommended)</span>
                  </label>
                  <input
                    type="text"
                    name="industry"
                    value={profile.industry}
                    onChange={handleChange}
                    
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., Dental Practice, HVAC, Real Estate"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Focus Keywords 🎯
                  </label>
                  <input
                    type="text"
                    name="focus_keywords"
                    value={profile.focus_keywords}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., dental implants, teeth whitening, family dentist, cosmetic dentistry, emergency dental care"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 <strong>Pro Tip:</strong> Add all search terms your customers use (comma-separated). Include variations, abbreviations, and alternative names for your services.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years in Business
                  </label>
                  <input
                    type="number"
                    name="years_in_business"
                    value={profile.years_in_business}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., 5"
                  />
                </div>
              </div>

              <div className="mt-6">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Business Description <span className="text-gray-400">(recommended)</span>
                  </label>
                  <Button
                    type="button"
                    onClick={generateDescription}
                    disabled={generatingDescription || !profile.business_name}
                    variant="outline"
                    size="sm"
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    {generatingDescription ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-3 h-3 mr-1" /> Generate with AI</>
                    )}
                  </Button>
                </div>
                <textarea
                  name="business_description"
                  value={profile.business_description}
                  onChange={handleChange}
                  
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter a few bullet points or notes, then click 'Generate with AI' to polish it. Or write your own description."
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Location</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-gray-400">(recommended)</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={profile.city}
                    onChange={handleChange}
                    
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., Austin"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-gray-400">(recommended)</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={profile.state}
                    onChange={handleChange}
                    
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g., TX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="(512) 555-0123"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={profile.address}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="123 Main St"
                  />
                </div>
              </div>
            </div>

            {/* Brand Voice & Expertise */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Brand Voice & Expertise</h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Voice / Tone <span className="text-gray-400">(recommended)</span>
                </label>
                <select
                  name="brand_voice"
                  value={profile.brand_voice}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly & Approachable</option>
                  <option value="authoritative">Authoritative & Expert</option>
                  <option value="energetic">Energetic & Motivating</option>
                  <option value="casual">Casual & Conversational</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner/Expert Name <span className="text-gray-400">(for expert quotes in content)</span>
                </label>
                <input
                  type="text"
                  name="owner_name"
                  value={profile.owner_name || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., John Smith, Master Kim, Dr. Sarah Johnson"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credentials & Experience <span className="text-gray-400">(builds authority in AI search)</span>
                </label>
                <textarea
                  name="expert_credentials"
                  value={profile.expert_credentials || ''}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., 4th Degree Black Belt, 20+ years teaching experience, Trained over 500 students, Certified USA Martial Arts Instructor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unique Selling Points <span className="text-gray-400">(what makes you different)</span>
                </label>
                <textarea
                  name="unique_selling_points"
                  value={profile.unique_selling_points}
                  onChange={handleChange}
                  
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., Family-owned since 2005, Free trial classes, Small class sizes (max 10 students), Award-winning youth program"
                />
              </div>
            </div>

            {/* Services, Areas & Audiences Section */}
            <div className="border-t border-gray-200 pt-8">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <h2 className="text-xl font-bold text-blue-900 mb-2">🎯 Services, Areas & Audiences</h2>
                <p className="text-sm text-blue-800">
                  <strong>This section powers your Question Discovery.</strong> The more specific you are, the better your AI-generated content will match what people search for.
                </p>
              </div>

              {/* Services List */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Services/Programs</h3>
                <p className="text-sm text-gray-500 mb-2">
                  Add each service or program <strong>one at a time</strong>. Type a service name, then click "Add" (or press Enter).
                </p>
                <p className="text-xs text-gray-400 mb-4">
                  💡 Example: Type "Kids Karate Classes" → click Add → Type "Adult Self Defense" → click Add
                </p>
                
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Type a service name here..."
                  />
                  <Button
                    type="button"
                    onClick={addService}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6"
                  >
                    + Add
                  </Button>
                </div>

                {profile.services_list.length === 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-orange-700">
                      <strong>⚠️ No services added yet.</strong> Add at least 3-5 services for best results.
                    </p>
                  </div>
                )}

                {profile.services_list.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Your services ({profile.services_list.length} added):</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.services_list.map((service, index) => (
                        <div key={index} className="bg-white border border-gray-300 rounded-lg px-4 py-2 flex items-center gap-2">
                          <span className="text-sm text-gray-700">{service}</span>
                          <button
                            type="button"
                            onClick={() => removeService(index)}
                            className="text-red-500 hover:text-red-700 font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Service Areas */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Service Areas (Cities/Neighborhoods)</h3>
                <p className="text-sm text-gray-500 mb-2">
                  Add each city or area <strong>one at a time</strong>. Type the name, then click "Add" (or press Enter).
                </p>
                <p className="text-xs text-gray-400 mb-4">
                  💡 Example: Type "Austin" → click Add → Type "Round Rock" → click Add
                </p>
                
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newServiceArea}
                    onChange={(e) => setNewServiceArea(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addServiceArea())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Type a city or neighborhood..."
                  />
                  <Button
                    type="button"
                    onClick={addServiceArea}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6"
                  >
                    + Add
                  </Button>
                </div>

                {profile.service_areas.length === 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-orange-700">
                      <strong>⚠️ No service areas added yet.</strong> Add the cities/areas you serve.
                    </p>
                  </div>
                )}

                {profile.service_areas.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Your service areas ({profile.service_areas.length} added):</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.service_areas.map((area, index) => (
                        <div key={index} className="bg-white border border-gray-300 rounded-lg px-4 py-2 flex items-center gap-2">
                          <span className="text-sm text-gray-700">{area}</span>
                          <button
                            type="button"
                            onClick={() => removeServiceArea(index)}
                            className="text-red-500 hover:text-red-700 font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Target Audiences */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Target Audiences</h3>
                <p className="text-sm text-gray-500 mb-2">
                  Add each audience type <strong>one at a time</strong>. Type the audience, then click "Add".
                </p>
                <p className="text-xs text-gray-400 mb-4">
                  💡 Example: Type "Parents of kids ages 5-12" → click Add → Type "Adults looking for fitness" → click Add
                </p>
                
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTargetAudience}
                    onChange={(e) => setNewTargetAudience(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTargetAudience())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Type an audience type..."
                  />
                  <Button
                    type="button"
                    onClick={addTargetAudience}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6"
                  >
                    + Add
                  </Button>
                </div>

                {profile.target_audiences_list.length === 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-orange-700">
                      <strong>⚠️ No audiences added yet.</strong> Add who you want to attract.
                    </p>
                  </div>
                )}

                {profile.target_audiences_list.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Your target audiences ({profile.target_audiences_list.length} added):</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.target_audiences_list.map((audience, index) => (
                        <div key={index} className="bg-white border border-gray-300 rounded-lg px-4 py-2 flex items-center gap-2">
                          <span className="text-sm text-gray-700">{audience}</span>
                          <button
                            type="button"
                            onClick={() => removeTargetAudience(index)}
                            className="text-red-500 hover:text-red-700 font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* CTA Library */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">📢 CTA Library (Calls to Action)</h3>
              <p className="text-sm text-gray-500 mb-4">
                Create CTAs for each program or audience. QuillFire will insert the best CTA based on the post topic—so your content converts.
              </p>
              
              {/* Add new CTA form */}
              <div className="bg-white rounded-lg p-4 mb-4 border border-purple-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="CTA Name (e.g., Kids Program)"
                    value={newCta.name}
                    onChange={(e) => setNewCta({...newCta, name: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Target Audience (e.g., Parents)"
                    value={newCta.audience}
                    onChange={(e) => setNewCta({...newCta, audience: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <Button
                    type="button"
                    onClick={addCta}
                    disabled={!newCta.name.trim() || !newCta.text.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    + Add CTA
                  </Button>
                </div>
                <div className="flex gap-2">
                  <textarea
                    placeholder="CTA Text will appear here..."
                    value={newCta.text}
                    onChange={(e) => setNewCta({...newCta, text: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    rows={2}
                  />
                  <Button
                    type="button"
                    onClick={generateCtaText}
                    disabled={generatingCta || !newCta.name.trim()}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white whitespace-nowrap"
                  >
                    {generatingCta ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</>
                    ) : (
                      '✨ Generate with AI'
                    )}
                  </Button>
                </div>
              </div>

              {/* Existing CTAs */}
              {profile.cta_library && profile.cta_library.length > 0 ? (
                <div className="space-y-3">
                  {profile.cta_library.map((cta) => (
                    <div key={cta.id} className="bg-white rounded-lg p-4 border border-gray-200">
                      {editingCta && editingCta.id === cta.id ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={editingCta.name}
                              onChange={(e) => setEditingCta({...editingCta, name: e.target.value})}
                              placeholder="CTA Name"
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <input
                              type="text"
                              value={editingCta.audience}
                              onChange={(e) => setEditingCta({...editingCta, audience: e.target.value})}
                              placeholder="Audience"
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <textarea
                            value={editingCta.text}
                            onChange={(e) => setEditingCta({...editingCta, text: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={saveEditCta}
                              className="px-3 py-1 bg-green-500 text-white rounded text-sm"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingCta(null)}
                              className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-purple-700">{cta.name}</span>
                              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                                {cta.audience}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{cta.text}</p>
                          </div>
                          <div className="flex gap-2 ml-2">
                            <button
                              type="button"
                              onClick={() => startEditCta(cta)}
                              className="text-purple-500 hover:text-purple-700 text-sm"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => removeCta(cta.id)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No CTAs added yet. Add your first CTA above!</p>
              )}
            </div>

            {/* Links Library */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">🔗 Links Library</h3>
                <Button
                  type="button"
                  onClick={generateLinksFromWebsite}
                  disabled={generatingLinks || !profile.website}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-sm"
                >
                  {generatingLinks ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Scanning...</>
                  ) : (
                    '✨ Auto-Generate from Website'
                  )}
                </Button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Save your key website pages so QuillFire can build an internal linking strategy automatically—helping SEO and guiding readers to your program pages.
              </p>
              
              {/* Add new Link form */}
              <div className="bg-white rounded-lg p-4 mb-4 border border-blue-100">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    placeholder="Link Name"
                    value={newLink.name}
                    onChange={(e) => setNewLink({...newLink, name: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="url"
                    placeholder="URL (https://...)"
                    value={newLink.url}
                    onChange={(e) => setNewLink({...newLink, url: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newLink.description}
                    onChange={(e) => setNewLink({...newLink, description: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <Button
                    type="button"
                    onClick={addLink}
                    disabled={!newLink.name.trim() || !newLink.url.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    + Add Link
                  </Button>
                </div>
              </div>

              {/* Existing Links */}
              {profile.links_library && profile.links_library.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {profile.links_library.map((link) => (
                    <div key={link.id} className="bg-white rounded-lg p-3 border border-gray-200">
                      {editingLink && editingLink.id === link.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editingLink.name}
                            onChange={(e) => setEditingLink({...editingLink, name: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <input
                            type="url"
                            value={editingLink.url}
                            onChange={(e) => setEditingLink({...editingLink, url: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={saveEditLink}
                              className="px-3 py-1 bg-green-500 text-white rounded text-sm"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingLink(null)}
                              className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-blue-700">{link.name}</span>
                            <a 
                              href={link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-500 hover:underline truncate block"
                            >
                              {link.url}
                            </a>
                          </div>
                          <div className="flex gap-2 ml-2">
                            <button
                              type="button"
                              onClick={() => startEditLink(link)}
                              className="text-blue-500 hover:text-blue-700 text-sm"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => removeLink(link.id)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No links added yet. Add your important links above!</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                <span className="text-gray-400">(recommended)</span> Required fields
              </p>
              <Button
                type="submit"
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 text-lg font-semibold"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save Profile
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
